import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navigation from '../shared/components/Navigation';
import Footer from '../shared/components/Footer';
import { dataService } from '../services/dataService';
import { authService } from '../config/supabase';
import './CustomerOrders.css';
import { formatCurrency, getUserCurrency, formatCurrencySync } from '../shared/utils/currency';
import useCurrencyCache from '../hooks/useCurrencyCache';
import '../styles/LoadingError.css';

const CustomerOrders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Use currency cache hook
  const { formatCurrency: formatCurrencyCached } = useCurrencyCache();
  const [error, setError] = useState(null);

  // Load orders data
  useEffect(() => {
    const loadOrders = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Get orders for authenticated user only
        let ordersData = [];
        let customerId = null;
        
        try {
          // First, try to get current authenticated user
          const user = await authService.getCurrentUser();
          console.log('Current authenticated user:', user?.email);
          
          if (user?.id || user?.email) {
            // Prefer lookup by auth UID; fallback to email
            let customerRecord = await dataService.getCustomerByAuthUid(user.id);
            if (!customerRecord && user?.email) {
              customerRecord = await dataService.getCustomerByEmail(user.email);
            }
            console.log('Customer record found:', customerRecord);
            
            if (customerRecord) {
              customerId = customerRecord.id;
              console.log('Using customer ID:', customerId);
              
              // Get orders for this specific customer
              ordersData = await dataService.getOrdersByCustomer(customerId);
              console.log('Orders found for customer:', ordersData.length);
            } else {
              console.log('No customer record found for authenticated user');
              throw new Error('Customer record not found. Please sign in again or place an order first.');
            }
          } else {
            console.log('No authenticated user found');
            throw new Error('Please sign in to view your orders.');
          }
        } catch (e) {
          console.error('Error getting user-specific orders:', e);
          // Don't show fallback orders - only show customer's own orders
          ordersData = [];
          console.log('No orders found for this customer');
        }
        
        // Transform the data to match the expected format with real items
        const transformedOrders = ordersData.map(order => ({
          id: order.id,
          orderNumber: `ORD-${order.id.toString().padStart(3, '0')}`,
          date: order.created_at ? order.created_at.split('T')[0] : new Date().toISOString().split('T')[0],
          status: order.status,
          items: (order.order_items || []).map(oi => ({
            name: oi.item_name,
            quantity: oi.quantity,
            price: oi.item_price,
            image: oi.item_image || oi.image || ''
          })),
          total: order.total ?? order.final_total ?? 0
        }));
        
        setOrders(transformedOrders);
      } catch (err) {
        console.error('Error loading orders:', err);
        setError('Failed to load orders. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadOrders();
  }, []);

  const getStatusColor = (status) => {
    return `order-status ${status}`;
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Show loading state
  if (loading) {
    return (
      <div className="App">
        <Navigation websiteType="customer" />
        <div className="orders-container">
          <div className="orders-content">
            <div className="loading-container">
              <div className="loading-spinner">⏳</div>
              <div className="loading-text">Loading orders...</div>
            </div>
          </div>
        </div>
        <Footer websiteType="customer" />
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="App">
        <Navigation websiteType="customer" />
        <div className="orders-container">
          <div className="orders-content">
            <div className="error-container">
              <div className="error-animation">
                <svg width="180" height="180" viewBox="0 0 180 180" fill="none" xmlns="http://www.w3.org/2000/svg">
                  {/* Sad Face Circle */}
                  <circle cx="90" cy="90" r="50" stroke="#ff6b6b" strokeWidth="4" fill="none" opacity="0.2">
                    <animate attributeName="opacity" values="0.2;0.4;0.2" dur="2s" repeatCount="indefinite"/>
                  </circle>
                  
                  {/* Eyes */}
                  <circle cx="75" cy="80" r="5" fill="#ff6b6b">
                    <animate attributeName="cy" values="80;82;80" dur="3s" repeatCount="indefinite"/>
                  </circle>
                  <circle cx="105" cy="80" r="5" fill="#ff6b6b">
                    <animate attributeName="cy" values="80;82;80" dur="3s" repeatCount="indefinite"/>
                  </circle>
                  
                  {/* Sad Mouth */}
                  <path d="M 75 110 Q 90 100 105 110" stroke="#ff6b6b" strokeWidth="3" fill="none" strokeLinecap="round"/>
                  
                  {/* Error Icon Background */}
                  <circle cx="130" cy="60" r="20" fill="#ff6b6b" opacity="0.1">
                    <animate attributeName="r" values="20;22;20" dur="2s" repeatCount="indefinite"/>
                  </circle>
                  
                  {/* X Mark */}
                  <line x1="122" y1="52" x2="138" y2="68" stroke="#ff6b6b" strokeWidth="3" strokeLinecap="round">
                    <animate attributeName="opacity" values="1;0.5;1" dur="1.5s" repeatCount="indefinite"/>
                  </line>
                  <line x1="138" y1="52" x2="122" y2="68" stroke="#ff6b6b" strokeWidth="3" strokeLinecap="round">
                    <animate attributeName="opacity" values="1;0.5;1" dur="1.5s" repeatCount="indefinite"/>
                  </line>
                  
                  {/* Broken Connection Lines */}
                  <line x1="40" y1="120" x2="55" y2="120" stroke="#ff6b6b" strokeWidth="2" strokeLinecap="round" opacity="0.4" strokeDasharray="5,5">
                    <animate attributeName="stroke-dashoffset" values="0;10" dur="1s" repeatCount="indefinite"/>
                  </line>
                  <line x1="125" y1="120" x2="140" y2="120" stroke="#ff6b6b" strokeWidth="2" strokeLinecap="round" opacity="0.4" strokeDasharray="5,5">
                    <animate attributeName="stroke-dashoffset" values="0;10" dur="1s" repeatCount="indefinite"/>
                  </line>
                </svg>
              </div>
              <div className="error-text">{error}</div>
              <button 
                className="retry-btn" 
                onClick={() => window.location.reload()}
              >
                🔄 Try Again
              </button>
            </div>
          </div>
        </div>
        <Footer websiteType="customer" />
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="App">
        <Navigation websiteType="customer" />
        
        <div className="orders-container">
          <div className="orders-content">
            <div className="empty-orders">
              <div className="empty-orders-animation">
                <svg width="200" height="200" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
                  {/* Clipboard */}
                  <rect x="50" y="40" width="100" height="130" rx="8" fill="#5B3EF0" opacity="0.1" stroke="#5B3EF0" strokeWidth="3">
                    <animate attributeName="opacity" values="0.1;0.15;0.1" dur="2s" repeatCount="indefinite"/>
                  </rect>
                  
                  {/* Clipboard Top */}
                  <rect x="75" y="30" width="50" height="15" rx="4" fill="#5B3EF0" opacity="0.2"/>
                  <circle cx="100" cy="37" r="3" fill="#fff"/>
                  
                  {/* Empty Lines (fading) */}
                  <line x1="70" y1="70" x2="130" y2="70" stroke="#5B3EF0" strokeWidth="2" strokeLinecap="round" opacity="0.3">
                    <animate attributeName="opacity" values="0.3;0.6;0.3" dur="2s" repeatCount="indefinite"/>
                  </line>
                  <line x1="70" y1="90" x2="130" y2="90" stroke="#5B3EF0" strokeWidth="2" strokeLinecap="round" opacity="0.25">
                    <animate attributeName="opacity" values="0.25;0.5;0.25" dur="2s" begin="0.3s" repeatCount="indefinite"/>
                  </line>
                  <line x1="70" y1="110" x2="130" y2="110" stroke="#5B3EF0" strokeWidth="2" strokeLinecap="round" opacity="0.2">
                    <animate attributeName="opacity" values="0.2;0.4;0.2" dur="2s" begin="0.6s" repeatCount="indefinite"/>
                  </line>
                  <line x1="70" y1="130" x2="110" y2="130" stroke="#5B3EF0" strokeWidth="2" strokeLinecap="round" opacity="0.15">
                    <animate attributeName="opacity" values="0.15;0.3;0.15" dur="2s" begin="0.9s" repeatCount="indefinite"/>
                  </line>
                  
                  {/* Delivery Bike */}
                  <g transform="translate(0, 0)">
                    <animateTransform attributeName="transform" type="translate" values="0,0; 5,0; 0,0" dur="3s" repeatCount="indefinite"/>
                    
                    {/* Bike Body */}
                    <circle cx="160" cy="150" r="8" stroke="#5B3EF0" strokeWidth="2" fill="none">
                      <animateTransform attributeName="transform" type="rotate" values="0 160 150; 360 160 150" dur="2s" repeatCount="indefinite"/>
                    </circle>
                    <path d="M160 142 L165 135 L175 135 L175 145" stroke="#5B3EF0" strokeWidth="2" fill="none"/>
                    
                    {/* Box */}
                    <rect x="168" y="130" width="15" height="15" fill="#5B3EF0" opacity="0.3" rx="2">
                      <animate attributeName="y" values="130;128;130" dur="3s" repeatCount="indefinite"/>
                    </rect>
                  </g>
                  
                  {/* Sparkle Stars */}
                  <path d="M40 60 L42 65 L47 67 L42 69 L40 74 L38 69 L33 67 L38 65 Z" fill="#5B3EF0" opacity="0">
                    <animate attributeName="opacity" values="0;0.8;0" dur="2s" repeatCount="indefinite"/>
                  </path>
                  <path d="M160 50 L161 53 L164 54 L161 55 L160 58 L159 55 L156 54 L159 53 Z" fill="#5B3EF0" opacity="0">
                    <animate attributeName="opacity" values="0;0.8;0" dur="2s" begin="0.7s" repeatCount="indefinite"/>
                  </path>
                  
                  {/* Question Marks */}
                  <text x="85" y="150" fontSize="16" fill="#5B3EF0" opacity="0.4">?
                    <animate attributeName="y" values="150;145;150" dur="2s" repeatCount="indefinite"/>
                  </text>
                  <text x="110" y="155" fontSize="14" fill="#5B3EF0" opacity="0.3">?
                    <animate attributeName="y" values="155;150;155" dur="2.3s" repeatCount="indefinite"/>
                  </text>
                </svg>
              </div>
              <h2 className="empty-orders-title">No orders yet</h2>
              <p className="empty-orders-subtitle">
                You haven't placed any orders yet. Start by browsing our delicious menu and place your first order!
              </p>
              <Link to="/menu" className="browse-menu-btn">
                🍽️ Browse Menu
              </Link>
            </div>
          </div>
        </div>
        
        <Footer websiteType="customer" />
      </div>
    );
  }

  return (
    <div className="App">
      <Navigation websiteType="customer" />
      
      <div className="orders-container">
        <div className="orders-content">
          <div className="orders-header">
            <h1 className="orders-title">My Orders</h1>
            <p className="orders-subtitle">Track your order history and current orders</p>
          </div>

          {orders.map((order) => (
            <div key={order.id} className="order-card">
              <div className="order-header">
                <div>
                  <div className="order-number">{order.orderNumber}</div>
                  <div className="order-date">{formatDate(order.date)}</div>
                </div>
                <span className={getStatusColor(order.status)}>
                  {order.status}
                </span>
              </div>

              <div className="order-items">
                {order.items.map((item, index) => (
                  <div key={index} className="order-item">
                    <img 
                      src={item.image} 
                      alt={item.name} 
                      className="order-item-image"
                    />
                    <div className="order-item-details">
                      <div className="order-item-name">{item.name}</div>
                      <div className="order-item-quantity">Qty: {item.quantity}</div>
                    </div>
                    <div className="order-item-price">{formatCurrencyCached(item.price, getUserCurrency())}</div>
                  </div>
                ))}
              </div>

              <div className="order-footer">
                <div className="order-total">Total: {formatCurrencyCached(order.total, getUserCurrency())}</div>
                <div className="order-actions">
                  {/* Always allow tracking so customers can see real-time updates */}
                  <button className="order-btn order-btn-primary" onClick={() => navigate(`/order/${order.id}`)}>
                    Track Order
                  </button>
                  {order.status === 'pending' && (
                    <button className="order-btn order-btn-danger">Cancel Order</button>
                  )}
                  {order.status === 'delivered' && (
                    <>
                      <button className="order-btn order-btn-secondary">Reorder</button>
                      <button className="order-btn order-btn-primary">Rate Order</button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      <Footer websiteType="customer" />
    </div>
  );
};

export default CustomerOrders;
